import { useState, useEffect, useContext } from 'react'
import { enqueueSnackbar } from 'notistack'
import _ from 'lodash'

import { AppContext } from '/providers/app-context'
import { updatePass } from '/providers/passes-api'

import getLeafNodes from '/utils/get-leaf-nodes'

import { Button, TextField } from '@mui/material'

const EditForm = ({ pass, templateId, setPass, dataSource="instance_data" }) => {

  const { client, apiSource, apiKey, setProcessing } = useContext(AppContext)

  const [editableFields, setEditableFields] = useState([])
  const [newData, setNewData] = useState({})
  const [originalInstanceData, setOriginalInstanceData] = useState({})
  const [limitedUpdates, setLimitedUpdates] = useState(false)

  useEffect(() => {
    if (pass) {
      setOriginalInstanceData(pass[dataSource])

      const ln = getLeafNodes(pass[dataSource])

      if (client.limitUpdates) { // Client is only permitted  a limited numnber of updates per 24 hours
        if (pass.update_counter >= client.updates_per_day) {
          setLimitedUpdates(true)
        }
      }

      const ef = ln.filter(n => {
        const branch = n.split(".").shift()
        if (branch.match(/__PASS_ID__/)) return false // Cannot change pass_id !!!
        if (branch.match(/__scanner_barcode__/)) return false // Cannot change pass_id !!!
        if (branch.match(/\*_note/)) return false // Cannot change pass_id !!!
        if (branch.match(/\*_disabled/)) return false // Cannot change pass_id !!!

        return true
      })

      setEditableFields(ef)

      let nd = _.cloneDeep(pass[dataSource])
      setNewData(nd)
    }
  }, [pass])


  const setField = (node, value) => {
    let d = _.cloneDeep(newData)
    _.set(d, node, value)
    setNewData(d)
  }

  const save = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Saving new pass data', variant: 'light' })
    const result = await updatePass({ apiSource, apiKey, templateId, passId: pass.pass_id, [dataSource]: newData })

    if (result.pass) {
      enqueueSnackbar({ message: 'New pass data saved', variant: 'success' })
      setPass(result.pass)
    } else {
      enqueueSnackbar({ message: (result.error.message ?? result.error) ?? "Unknown error", variant: 'error' })
    }

    setProcessing(false)
  }

  if (limitedUpdates) return (
    <div className='flex flex-col gap-5 pt-10'>
      <div>
        This pass has been edited and pushed to devices or a notification sent recently. Please check back later.
      </div>

      <div>
        Since you are using Smartix signing certifiates, you are limited to one pass update per 24 hours.
      </div>

      <div>
        If you require more frequent pass updates / notifications then please register your own Apple and Google developer accounts
        and provide us with your own signing certificates.
      </div>

    </div>

  )

  return (
    <div className='flex flex-col gap-5 pt-10'>
      {editableFields.filter(node => !["__pass_id__","__scanner_barcode__","*_note","*_disabled"].includes(node)).map(node =>
        <EditField key={node} node={node} originalInstanceData={originalInstanceData} newData={newData} setField={setField} />
      )}
      <div className='mt-10 flex w-full justify-between gap-10'>
        <div>Ensure your data is correct before you save. When you save, this pass will be updated and
          the new content will be pushed to any devices this pass is installed on. Pay special attention
          to <b>dates and times</b> to ensure they are in the correct format, and that mail / telephone links <b>do not include
            mailto: / tel:</b>, but that web links do include <b>http://</b> or <b>https://</b>. Failure to ensure this data is
          correct can result in incorrect display and/or functionality of this pass.
        </div>
        <Button onClick={save} variant='contained' disabled={_.isEqual(originalInstanceData, newData)}>Save</Button>
      </div>
    </div>
  )
}
export default EditForm

const EditField = ({ node, originalInstanceData, newData, setField }) => {
  try {
    const originalValue = _.get(originalInstanceData, node).toString()

    const isImage = originalValue ? originalValue.match(/s3\.eu-west-1\.amazonaws.com\/images.smartix.uk/)?.length > 0 : false

    return (
      <div className='flex flex-row gap-5'>

        {!isImage && <TextField className='w-full' value={_.get(newData, node)} label={node} helperText={"original value: " + originalValue} onChange={e => setField(node, e.target.value)} />}

      </div>
    )
  }
  catch (err) {
    console.log(err)
  }
}